import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PhoneCall } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DiscoveryCallToggleProps {
  mentorId: string;
  discoveryCallEnabled: boolean;
  onToggleChange?: (enabled: boolean) => void;
  showCard?: boolean;
}

export const DiscoveryCallToggle: React.FC<DiscoveryCallToggleProps> = ({
  mentorId,
  discoveryCallEnabled,
  onToggleChange,
  showCard = true
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  const handleToggle = async (checked: boolean) => {
    setIsUpdating(true);
    
    try {
      const { error } = await supabase
        .from('mentors')
        .update({ discovery_call_enabled: checked })
        .eq('id', mentorId);

      if (error) throw error;

      onToggleChange?.(checked);
      
      toast({
        title: checked ? "Discovery Calls Enabled" : "Discovery Calls Disabled",
        description: checked 
          ? "Students can now book a free 30-minute consultation with you." 
          : "Students can no longer book free discovery calls.",
      });
    } catch (error) {
      console.error('Error updating discovery call setting:', error);
      toast({
        title: "Error",
        description: "Failed to update discovery call setting. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const content = (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-2">
          <PhoneCall className={`h-5 w-5 ${discoveryCallEnabled ? 'text-mentor-primary' : 'text-muted-foreground'}`} />
          <div>
            <Label htmlFor="discovery-call-toggle" className="text-sm font-medium">
              Offer Free Discovery Calls
            </Label>
            <p className="text-xs text-muted-foreground">
              {discoveryCallEnabled 
                ? "Students can book one free 30-min consultation" 
                : "Discovery calls are not offered"
              }
            </p>
          </div>
        </div>
      </div>
      <Switch
        id="discovery-call-toggle"
        checked={discoveryCallEnabled}
        onCheckedChange={handleToggle}
        disabled={isUpdating}
      />
    </div>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card className="obsidian-card">
      <CardHeader>
        <CardTitle className="text-energy flex items-center gap-2">
          <PhoneCall className="h-5 w-5" />
          Discovery Calls
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};
